import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DevLoginBody, DevLoginUser } from '@product-portal/shared';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SelectField } from '@/components/portal/select-field';

/** Raw Entra values the simulated token carries; the API maps them exactly like a real token. */
const ROLE_CHOICES = [
  { label: 'Demandeur — aucun rôle', value: '' },
  { label: 'Référent — product_portal.referent', value: 'product_portal.referent' },
  { label: 'PO — product_portal.po', value: 'product_portal.po' },
  { label: 'Rôle mal orthographié — product-portal.po', value: 'product-portal.po' },
] as const;

/**
 * Development login provider (NODE_ENV=development only — the API refuses it
 * elsewhere). Not part of the mock-ups: a developer tool, never shown in
 * deployed environments.
 */
export function DevLoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['dev-users'], queryFn: () => apiFetch<DevLoginUser[]>('/api/auth/dev/users'), retry: false });
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<string>('');

  const login = useMutation({
    mutationFn: (body: DevLoginBody) => apiFetch<{ role: string }>('/api/auth/dev/login', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      navigate('/');
    },
  });

  if (users.isError) {
    return <p className="p-8 text-sm text-muted-foreground">Le fournisseur de développement n'est pas disponible sur cet environnement.</p>;
  }

  const selected = userId || users.data?.[0]?.id || '';
  return (
    <div className="mx-auto flex max-w-[640px] flex-col gap-6 px-6 py-12">
      <img src="/product-portal-logo.png" alt="Product Portal — Europa Group" className="block h-10 w-auto self-start" />
      <Card>
        <CardHeader>
          <CardTitle>Connexion de développement</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="m-0 text-[13px] text-muted-foreground">
            Simule un jeton Entra : utilisateur du seed et valeurs de la revendication roles. Disponible uniquement avec NODE_ENV=development.
          </p>
          <SelectField label="Utilisateur" value={selected} onChange={(e) => setUserId(e.target.value)} disabled={!users.data}>
            {users.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName} — {u.teams.length ? u.teams.join(', ') : 'sans équipe'}
                {u.lastSeenRoles.length ? ` (${u.lastSeenRoles.join(', ')})` : ''}
              </option>
            ))}
          </SelectField>
          <SelectField label="Rôles du jeton" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </SelectField>
          <div>
            <Button disabled={!selected || login.isPending} onClick={() => login.mutate({ userId: selected, entraRoles: role ? [role] : [] })}>
              Se connecter
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
